import React, { useEffect, useRef, useState } from "react";
import { sendChat } from "../lib/api";
import { LevelLanguage, Loader } from "../components/Shared";
import { Send, RotateCcw } from "lucide-react";

const SUGGESTIONS = [
  "Tell me about Icarus in 3 lines.",
  "How does Newton's Disc work?",
  "Story about the Tortoise and the Hare",
  "Explain Total Internal Reflection with everyday example",
  "What is the moral of the Lion and the Mouse?",
  "How do magnets attract iron?",
];

export default function Chat() {
  const [messages, setMessages] = useState([
    {
      role: "assistant",
      content:
        "Hello! I'm your EduSphere companion. Ask me about world mythology, the 50 STEM models, or any concept you're curious about. Where shall we begin?",
    },
  ]);
  const [input, setInput] = useState("");
  const [level, setLevel] = useState("school");
  const [language, setLanguage] = useState("english");
  const [sessionId, setSessionId] = useState(null);
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, loading]);

  const send = async (text) => {
    const message = (text ?? input).trim();
    if (!message || loading) return;
    setMessages((m) => [...m, { role: "user", content: message }]);
    setInput("");
    setLoading(true);
    try {
      const res = await sendChat({ session_id: sessionId, message, level, language });
      setSessionId(res.session_id);
      setMessages((m) => [...m, { role: "assistant", content: res.reply }]);
    } catch (e) {
      setMessages((m) => [
        ...m,
        { role: "assistant", content: "Sorry, I had trouble responding. Please try again." },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setSessionId(null);
    setMessages([
      {
        role: "assistant",
        content:
          "Fresh start! What would you like to explore — a story, a concept, or a quick question?",
      },
    ]);
  };

  return (
    <div className="max-w-5xl mx-auto px-5 md:px-10 py-8 md:py-12" data-testid="chat-page">
      <div className="flex items-end justify-between gap-4 flex-wrap mb-6">
        <div>
          <div className="text-[11px] tracking-[0.22em] uppercase text-[#3A5A40] font-semibold mb-2">Chat tutor</div>
          <h1 className="font-heading text-3xl md:text-4xl font-bold tracking-tight">Ask, learn, explore.</h1>
        </div>
        <button onClick={reset} className="btn-outline inline-flex items-center gap-2" data-testid="chat-reset">
          <RotateCcw size={14} /> New chat
        </button>
      </div>

      <div className="mb-5">
        <LevelLanguage level={level} setLevel={setLevel} language={language} setLanguage={setLanguage} />
      </div>

      {/* Suggestions */}
      <div className="flex flex-wrap gap-2 mb-5" data-testid="chat-suggestions">
        {SUGGESTIONS.map((s, i) => (
          <button
            key={i}
            data-testid={`suggestion-${i}`}
            onClick={() => send(s)}
            className="text-xs bg-white border border-[#DCD7CE] hover:border-[#E07A5F] px-3 py-1.5 rounded-full text-[#1A1A1A] transition"
          >
            {s}
          </button>
        ))}
      </div>

      {/* Messages */}
      <div
        ref={scrollRef}
        className="card p-5 md:p-7 h-[58vh] overflow-y-auto thin-scroll space-y-4"
        data-testid="chat-messages"
      >
        {messages.map((m, i) => (
          <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
            <div
              className={`max-w-[88%] sm:max-w-[78%] whitespace-pre-wrap text-[15px] leading-relaxed ${
                m.role === "user" ? "bubble-user" : "bubble-ai"
              }`}
              data-testid={`msg-${m.role}-${i}`}
            >
              {m.content}
            </div>
          </div>
        ))}
        {loading && (
          <div className="flex justify-start">
            <div className="bubble-ai">
              <Loader label="Composing your answer…" />
            </div>
          </div>
        )}
      </div>

      {/* Composer */}
      <form
        className="mt-5 flex gap-3 items-center"
        onSubmit={(e) => {
          e.preventDefault();
          send();
        }}
        data-testid="chat-composer"
      >
        <input
          className="input-field"
          placeholder="Ask about Icarus, Newton's Disc, careers in design…"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          data-testid="chat-input"
        />
        <button
          type="submit"
          disabled={loading || !input.trim()}
          className="btn-primary inline-flex items-center gap-2 disabled:opacity-50"
          data-testid="chat-send"
        >
          <Send size={16} /> Send
        </button>
      </form>
    </div>
  );
}
